<template>
    <div>
        <div
            @click="handleToggle()"
            class="border border-stone-200 py-2 px-3 rounded-lg2 cursor-pointer"
            :class="{
                'bg-stone-100 text-gray-500 select-none cursor-not-allowed':
                    disabled,
                'bg-white': !disabled,
            }"
        >
            <div class="flex items-center justify-between">
                <div>
                    {{ selectedEntity.displayText }}
                </div>
                <div v-if="!disabled">
                    <Icon
                        :name="isExpanded ? 'angle-up' : 'angle-down'"
                        class="w-4 h-4 text-gray-500"
                    />
                </div>
            </div>

            <div
                v-if="isExpanded"
                class="mt-2 space-y-2"
            >
                <div class="flex items-center gap-1">
                    <TextInput
                        :id="randomId"
                        v-model="searchTerm"
                        placeholder="Search"
                        @click.stop
                        @update:modelValue="handleSeachTimezones($event)"
                        :useBookingFormThemeCssVariables="
                            useBookingFormThemeCssVariables
                        "
                    />
                    <Button
                        color="light"
                        iconName="arrow-rotate"
                        iconClass="w-4 h-4 text-gray-500"
                        @click.stop
                        @click="resetResults()"
                    />
                </div>
                <div class="max-h-48 min-h-48 overflow-y-auto">
                    <div v-if="localList.length > 0">
                        <template
                            v-for="item in localList"
                            :key="item.id"
                        >
                            <div
                                @click="handleItemSelectionChange(item.value)"
                                class="p-2 hover:bg-gray-100 rounded-lg2 text-sm"
                            >
                                {{ item.displayText }}
                            </div>
                        </template>
                    </div>
                    <div
                        v-else
                        class="text-center italic text-gray-500 text-sm"
                        @click.stop
                    >
                        No results...
                    </div>
                </div>
            </div>

            <input
                v-if="name"
                type="hidden"
                :name="name"
                :value="value"
                :required="required"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { computed, nextTick, ref, type PropType } from 'vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import Button from '@/Shared/Component/Button.vue'
    import useId from '@/Shared/Hook/useId'
    import useElementFocus from '@/Shared/Hook/useElementFocus'

    export interface SearcheableEntity {
        id: string
        value: string
        displayText: string
    }

    const emit = defineEmits<{
        onSelectionChange: [{ value: string }]
    }>()

    const props = defineProps({
        list: {
            type: Array as PropType<SearcheableEntity[]>,
            required: true,
        },
        name: {
            type: String,
            default: null,
        },
        value: {
            type: String,
            default: null,
        },
        useBookingFormThemeCssVariables: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            required: true,
        },
    })

    const searchTerm = ref<string>('')
    const localList = ref<SearcheableEntity[]>(props.list)
    const isExpanded = ref<boolean>(false)

    const handleToggle = (): void => {
        if (props.disabled) {
            return
        }

        isExpanded.value = !isExpanded.value

        if (!isExpanded.value) {
            return
        }

        focusSearchInput()
    }

    const handleItemSelectionChange = (value: string | null) => {
        if (!value) {
            return
        }

        emit('onSelectionChange', {
            value: value,
        })

        resetResults()
    }

    const handleSeachTimezones = (searchTerm: string | null): void => {
        if (!searchTerm) {
            localList.value = props.list

            return
        }

        localList.value = props.list.filter((item: SearcheableEntity) => {
            return item.displayText
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        })
    }

    const resetResults = () => {
        localList.value = props.list
        searchTerm.value = ''
    }

    const selectedEntity = computed((): SearcheableEntity => {
        return getItemByValue(props.value)
    })

    const getItemByValue = (value: string): SearcheableEntity => {
        const entity = props.list.find((item: SearcheableEntity) => {
            return item.value === value
        })

        if (!entity) {
            throw new Error(`Missing entity for value ${value}`)
        }

        return entity
    }

    // ---------------
    // Focus
    // ---------------

    const randomId = useId().make()

    const focusSearchInput = (): void => {
        nextTick(() => {
            useElementFocus().focus(randomId)
        })
    }
</script>
